import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Input, Button } from 'antd';
import Recaptcha from 'react-google-invisible-recaptcha';

import { G_RECAPTCHA_SITE_KEY } from '../../../envVariables';
import { requestQuote } from '../../../actions/rate_gate_actions';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** @readonly @enum {string} Submission lifecycle (never compare raw strings). */
const Phase = Object.freeze({
  IDLE: 'idle',
  SUBMITTING: 'submitting',
  DONE: 'done',
  ERROR: 'error',
});

/**
 * @component
 * @category Search Page
 * @description Zero-result capture CTA — rendered inside {@link NoResult} when a
 * searched lane has no published rate. Collects the visitor's email (prefilled
 * from the unlock, editable) plus an optional note and asks Prime Freight to
 * quote the lane manually. Self-contained: runs its OWN invisible reCAPTCHA on
 * every submit (per-request bot-check — the pf-comms proxy re-verifies the
 * token server-side, captures the lead, and alerts sales), and dispatches the
 * `requestQuote` thunk which attaches the live SearchContext. No useEffect; the
 * email seed comes from props at mount, and phase transitions are event-driven.
 * <br><br>
 * **Example**
 * ```js
 *  <QuoteRequestCard />
 * ```
 */
class QuoteRequestCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: props.userEmailBlurCards || '',
      note: '',
      phase: Phase.IDLE,
      errorMessage: null,
    };
    this.recaptcha = null;
    // Guards a resolved captcha promise against a stale submit (email edited
    // mid-flight) — mirrors BlurCards' pendingEmail pattern.
    this.pendingEmail = null;
  }

  onEmailChange = (e) => {
    const email = e.target.value;
    // Editing after an error clears it so the field isn't stuck red.
    this.setState((prev) => ({
      email,
      phase: prev.phase === Phase.ERROR ? Phase.IDLE : prev.phase,
      errorMessage: prev.phase === Phase.ERROR ? null : prev.errorMessage,
    }));
  };

  onNoteChange = (e) => this.setState({ note: e.target.value });

  fail = (errorMessage) => this.setState({ phase: Phase.ERROR, errorMessage });

  /**
   * Execute the invisible reCAPTCHA (its resolved value IS the token — the
   * primary, verified-reliable path), then dispatch the quote request. If the
   * widget is unavailable (script blocked / key missing) we submit with an
   * empty token and let the server-side verification decide, exactly like the
   * unlock flow — so a blocked badge never dead-ends a real customer.
   */
  executeAndSubmit = async (email) => {
    this.pendingEmail = email;
    let token = '';
    if (this.recaptcha?.execute) {
      try {
        token = (await this.recaptcha.execute()) || '';
      } catch (error) {
        token = '';
      }
      if (this.recaptcha.reset) this.recaptcha.reset();
    }
    // A later edit + resubmit supersedes this in-flight one; drop the stale result.
    if (this.pendingEmail !== email) return;

    const result = await this.props.requestQuote({
      email,
      captchaToken: token,
      note: this.state.note.trim() || undefined,
    });

    if (this.pendingEmail !== email) return;
    if (result && result.ok) this.setState({ phase: Phase.DONE, errorMessage: null });
    else this.fail((result && result.error) || 'We could not send your request. Please try again.');
  };

  onSubmit = () => {
    if (this.state.phase === Phase.SUBMITTING) return;
    const email = (this.state.email || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.fail('Please enter a valid email address.');
      return;
    }
    this.setState({ phase: Phase.SUBMITTING, errorMessage: null });
    this.executeAndSubmit(email);
  };

  render() {
    const { phase, email, note, errorMessage } = this.state;

    if (phase === Phase.DONE) {
      return (
        <div className="quote-request-card quote-request-card--done" style={styles.wrap}>
          <h4 style={styles.doneTitle}>Thanks — your request is on its way.</h4>
          <p style={styles.doneBody}>
            Our team will price this lane and email you a manual quote shortly.
          </p>
        </div>
      );
    }

    const submitting = phase === Phase.SUBMITTING;

    return (
      <div className="quote-request-card" style={styles.wrap}>
        <p style={styles.prompt}>
          No published rate for this lane yet — request a manual quote and our team will price it
          for you.
        </p>
        <div style={styles.row}>
          <Input
            type="email"
            value={email}
            onChange={this.onEmailChange}
            placeholder="you@company.com"
            disabled={submitting}
            onPressEnter={this.onSubmit}
            style={styles.input}
          />
          <Button type="primary" onClick={this.onSubmit} loading={submitting} style={styles.button}>
            Request a quote
          </Button>
        </div>
        <Input.TextArea
          value={note}
          onChange={this.onNoteChange}
          placeholder="Add any details (cargo, timing, volume)…  (optional)"
          disabled={submitting}
          autosize={{ minRows: 2, maxRows: 4 }}
          style={styles.note}
        />
        {phase === Phase.ERROR && errorMessage ? (
          <p style={styles.error} role="alert">
            {errorMessage}
          </p>
        ) : null}
        {G_RECAPTCHA_SITE_KEY ? (
          <Recaptcha
            ref={(ref) => {
              this.recaptcha = ref;
            }}
            sitekey={G_RECAPTCHA_SITE_KEY}
            badge="bottomright"
          />
        ) : null}
      </div>
    );
  }
}

const styles = {
  wrap: { maxWidth: 480, margin: '0 auto', textAlign: 'left' },
  prompt: { fontSize: 15, color: '#43637c', marginBottom: 12, textAlign: 'center', lineHeight: '22px' },
  row: { display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  input: { flex: '1 1 220px' },
  button: { flex: '0 0 auto' },
  note: { marginBottom: 8 },
  error: { color: '#d4380d', fontSize: 13, margin: 0 },
  doneTitle: { color: '#237804', fontWeight: 600, textAlign: 'center', marginBottom: 8 },
  doneBody: { color: '#43637c', fontSize: 15, textAlign: 'center', margin: 0, lineHeight: '22px' },
};

QuoteRequestCard.propTypes = {
  /** Prefill email captured at unlock (redux-persist cache). */
  userEmailBlurCards: PropTypes.string,
  /** requestQuote thunk (injected). */
  requestQuote: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  userEmailBlurCards: state.cache.userEmailBlurCards,
});

export default connect(mapStateToProps, { requestQuote })(QuoteRequestCard);
